import { useEffect, useMemo, useState, type ReactNode } from "react";
import type { AnswerSheet, ImpactResult, Policy, Profile } from "@contracts";
import { calculateImpact } from "@domain";
import { Bookmark, Check, Clock, Source as SourceIcon } from "./icons";
import { readProfile, readWatchlist, toggleWatch, writeProfile } from "./storage";
import { track } from "./analytics";
import { PortfolioThemeControl } from "./PortfolioThemeControl";

const stageLabel: Record<Policy["stage"], string> = {
  proposal: "발의·예고",
  committee: "심사 중",
  passed: "의결",
  promulgated: "공포",
  notified: "결정·고시",
  effective: "시행 중",
  repealed: "폐지",
  source_capability: "공식 출처"
};

const relevanceLabel: Record<ImpactResult["relevance"], string> = {
  applies: "적용",
  likely: "관련 가능성 높음",
  unknown: "추가 확인",
  not_applies: "현재 조건상 비적용"
};

export function StagePill({policy}: {policy: Policy}) {
  return <span className={`stage stage--${policy.stage}`}><span className="stage__dot" />{stageLabel[policy.stage]}</span>;
}

export function PolicyTrack({policy}: {policy: Policy}) {
  const stages = policy.stage === "notified" ? ["안", "의견·이의", "결정", "고시", "적용"] : ["발의", "심사", "의결", "공포", "시행"];
  const index = policy.stage === "proposal" ? 0 : policy.stage === "committee" ? 1 : policy.stage === "passed" ? 2 : policy.stage === "promulgated" || policy.stage === "notified" ? 3 : 4;
  return <div className="policy-track" aria-label={`정책 단계: ${stageLabel[policy.stage]}`}>
    {stages.map((stage, itemIndex) => <div className={`policy-track__item ${itemIndex <= index ? "is-done" : ""}`} key={stage}>
      <span>{itemIndex < index ? <Check /> : itemIndex + 1}</span><small>{stage}</small>
    </div>)}
  </div>;
}

export function WatchButton({policyId}: {policyId: string}) {
  const [watched, setWatched] = useState(() => readWatchlist().includes(policyId));
  useEffect(() => {
    const update = () => setWatched(readWatchlist().includes(policyId));
    window.addEventListener("bakkwimpyo:watchlist", update);
    return () => window.removeEventListener("bakkwimpyo:watchlist", update);
  }, [policyId]);
  return <button className={`watch-button ${watched ? "is-watched" : ""}`} type="button" onClick={() => {setWatched(toggleWatch(policyId).includes(policyId)); track("watchlist_toggled", {policyId});}} aria-pressed={watched}>
    <Bookmark />{watched ? "관심 정책에서 빼기" : "변화 지켜보기"}
  </button>;
}

export function SourceList({policy}: {policy: Policy}) {
  return <div className="source-list">
    {policy.sources.map((source) => <a className="source-row" href={source.url} target="_blank" rel="noreferrer" onClick={() => track("source_opened", {policyId: policy.id})} key={source.id}>
      <SourceIcon />
      <span><b>{source.publisher}</b><small>{source.title} · {source.checkedAt} 확인</small></span>
      <span aria-hidden="true">↗</span>
    </a>)}
  </div>;
}

export function ImpactLedger({policy, result}: {policy: Policy; result: ImpactResult}) {
  return <section className="impact-ledger" aria-labelledby="impact-heading">
    <header className="impact-ledger__head">
      <div><small>내 영향표</small><h2 id="impact-heading">{result.headline}</h2></div>
      <span className={`relevance relevance--${result.relevance}`}>{relevanceLabel[result.relevance]}</span>
    </header>
    <p className="impact-ledger__reason">{result.relevanceReason}</p>
    <div className="impact-table" role="table" aria-label="정책 변경 전후 비교. 작은 화면에서는 좌우로 스크롤할 수 있습니다." tabIndex={0}>
      <div className="impact-table__row impact-table__header" role="row">
        <span role="columnheader">항목</span><span role="columnheader">지금</span><span role="columnheader">바뀐 뒤</span><span role="columnheader">차이</span>
      </div>
      {result.lines.map((line) => <div className="impact-table__row" role="row" key={`${line.axis}-${line.label}`}>
        <span role="cell"><b>{line.label}</b><small>{line.numeric ? "계산됨" : "확인 필요"}</small></span>
        <span role="cell">{line.before}</span><span role="cell">{line.after}</span><strong role="cell">{line.delta}</strong>
      </div>)}
    </div>
    <PortfolioThemeControl />
    {(result.assumptions.length > 0 || result.needsConfirmation.length > 0) && <div className="impact-notes">
      {result.assumptions.map((item) => <p key={item}><Check />{item}</p>)}
      {result.needsConfirmation.map((item) => <p key={item}><Clock />{item}</p>)}
    </div>}
    <div className="impact-ledger__foot"><span>기준일 {policy.reviewedAt}</span><a href={`/policy/${policy.slug}`}>계산 근거와 원문 보기</a></div>
  </section>;
}

const confidenceLabel: Record<AnswerSheet["confidence"], string> = {
  official_confirmed: "공식 정책 확인",
  guide_match: "공식 확인 순서 안내",
  needs_context: "조건 확인 필요"
};

export function PolicyAnswerSheet({answer, savedActionLabels = new Set(), onSaveAction}: {answer: AnswerSheet; savedActionLabels?: Set<string>; onSaveAction?: (action: AnswerSheet["nextActions"][number]) => void}) {
  return <article className="answer-sheet" aria-live="polite">
    <header className="answer-sheet__query"><span>내 질문</span><p>{answer.query}</p></header>
    <div className="answer-sheet__conclusion">
      <div><span className={`answer-state answer-state--${answer.outcome}`}>{confidenceLabel[answer.confidence]}</span><h2>먼저 답하면</h2><strong>{answer.title}</strong><p>{answer.summary}</p></div>
      <span className="answer-sheet__stamp">{answer.outcome === "answer" ? "확인" : answer.outcome === "clarify" ? "조건" : "공식 확인"}</span>
    </div>
    <div className="answer-sheet__body">
      <section><h3>확인한 것</h3>{answer.facts.map((fact) => <p key={fact}><Check />{fact}</p>)}</section>
      {answer.unknowns.length > 0 && <section><h3>더 확인할 것</h3>{answer.unknowns.map((unknown) => <p key={unknown}><Clock />{unknown}</p>)}</section>}
    </div>
    <section className="answer-actions"><h3>지금 할 수 있는 일</h3>{answer.nextActions.map((action) => <div className="answer-action-row" key={`${action.label}-${action.url}`}>{action.url ? <a href={action.url} target={action.url.startsWith("http") ? "_blank" : undefined} rel={action.url.startsWith("http") ? "noreferrer" : undefined}>{action.label}<span aria-hidden="true">→</span></a> : <span>{action.label}</span>}{onSaveAction && <button type="button" disabled={savedActionLabels.has(action.label)} onClick={() => onSaveAction(action)}>{savedActionLabels.has(action.label) ? "할 일에 저장됨" : "할 일에 저장"}</button>}</div>)}</section>
    <details className="answer-sources"><summary>왜 이렇게 답했나요?</summary>{answer.citations.map((citation) => <a href={citation.url} target="_blank" rel="noreferrer" key={`${citation.publisher}-${citation.url}`}><SourceIcon /><span><b>{citation.publisher}</b><small>{citation.title} · {citation.checkedAt} 확인</small></span><span aria-hidden="true">↗</span></a>)}</details>
  </article>;
}

const policyGroups: Array<{value: Profile["employment"]; label: string}> = [
  {value: "employee", label: "직장인·근로자"},
  {value: "self_employed", label: "자영업자"},
  {value: "student", label: "학생·취업준비"},
  {value: "retired", label: "은퇴·연금생활"},
  {value: "caregiver", label: "부모·돌봄가족"},
  {value: "other", label: "그 밖의 생활"}
];

export function QuickProfile({policy, onComplete}: {policy: Policy; onComplete: (profile: Profile, result: ImpactResult) => void}) {
  const [profile, setProfile] = useState<Profile>(() => readProfile());
  const update = (patch: Partial<Profile>) => setProfile((current) => ({...current, ...patch}));
  const isWage = policy.calculatorId === "minimum_wage_2027";
  const isPension = policy.calculatorId.startsWith("pension_");
  const ready = profile.employment !== "unknown" && (!isWage || profile.monthlyPaidHours !== null) && (!isPension || (profile.pensionCovered !== null && (profile.pensionCovered === false || (profile.monthlyIncomeKrw !== null && profile.workplacePension !== null))));

  const submit = () => {
    const saved = writeProfile(profile);
    track("impact_generated", {policyId: policy.id, resultState: "calculated"});
    onComplete(saved, calculateImpact(policy, saved));
  };

  return <div className="quick-profile">
    <fieldset><legend>나는 지금 어디에 가까운가요?</legend><div className="chip-row">
      {policyGroups.map((group) => <button type="button" className={profile.employment === group.value ? "chip is-selected" : "chip"} onClick={() => update({employment: group.value})} key={group.value}>{group.label}</button>)}
    </div></fieldset>
    {isWage && <label className="field"><span>한 달 유급시간</span><input inputMode="numeric" min="0" max="744" value={profile.monthlyPaidHours ?? ""} onChange={(event) => update({monthlyPaidHours: event.target.value === "" ? null : Number(event.target.value)})} placeholder="예: 209" /><small>근로계약서의 유급시간을 넣으세요. 잘 모르면 209시간 예시를 볼 수 있어요.</small></label>}
    {isPension && <>
      <fieldset><legend>국민연금에 가입돼 있나요?</legend><div className="chip-row"><button type="button" className={profile.pensionCovered === true ? "chip is-selected" : "chip"} onClick={() => update({pensionCovered: true})}>가입</button><button type="button" className={profile.pensionCovered === false ? "chip is-selected" : "chip"} onClick={() => update({pensionCovered: false})}>미가입·모름</button></div></fieldset>
      {profile.pensionCovered && <><label className="field"><span>기준소득월액 또는 월 소득</span><input inputMode="numeric" value={profile.monthlyIncomeKrw ?? ""} onChange={(event) => update({monthlyIncomeKrw: event.target.value === "" ? null : Number(event.target.value)})} placeholder="예: 3090000" /><small>정확한 기준소득월액은 국민연금 고지에서 확인하세요.</small></label>
      <fieldset><legend>사업장가입자인가요?</legend><div className="chip-row"><button type="button" className={profile.workplacePension === true ? "chip is-selected" : "chip"} onClick={() => update({workplacePension: true})}>사업장가입</button><button type="button" className={profile.workplacePension === false ? "chip is-selected" : "chip"} onClick={() => update({workplacePension: false})}>지역가입</button></div></fieldset></>}
    </>}
    <div className="privacy-inline"><span className="privacy-inline__mark">로컬</span><span>입력값은 이 브라우저에만 저장됩니다. 계산 API로 보내지 않아도 작동합니다.</span></div>
    <button className="primary-button primary-button--wide" type="button" disabled={!ready} onClick={submit}>내 영향표 만들기</button>
  </div>;
}

export function FirstValueFlow({policies}: {policies: Policy[]}) {
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedId, setSelectedId] = useState(policies[0]?.id ?? "");
  const [result, setResult] = useState<ImpactResult | null>(null);
  const selected = useMemo(() => policies.find((policy) => policy.id === selectedId) ?? policies[0], [policies, selectedId]);
  if (!selected) return null;
  return <section className="first-value" id="start" aria-labelledby="start-heading">
    <div className="section-kicker">90초 영향 확인</div>
    <div className="stepper" aria-label={`3단계 중 ${step}단계`}><span className={step >= 1 ? "is-active" : ""}>1 정책</span><span className={step >= 2 ? "is-active" : ""}>2 내 조건</span><span className={step >= 3 ? "is-active" : ""}>3 영향표</span></div>
    <h2 id="start-heading">{step === 1 ? "어떤 변화부터 볼까요?" : step === 2 ? "계산에 꼭 필요한 것만 물을게요" : "내 생활에서 달라지는 것"}</h2>
    {step === 1 && <><div className="policy-pick-list">
      {policies.slice(0, 4).map((policy) => <button type="button" className={selectedId === policy.id ? "policy-pick is-selected" : "policy-pick"} onClick={() => {setSelectedId(policy.id); track("policy_selected", {policyId: policy.id, family: policy.family});}} key={policy.id}>
        <span><StagePill policy={policy} /><b>{policy.shortTitle}</b><small>{policy.summary}</small></span><span className="radio-mark" aria-hidden="true" />
      </button>)}
    </div><button className="primary-button" type="button" onClick={() => setStep(2)}>이 정책으로 확인하기</button></>}
    {step === 2 && <><button className="text-button" type="button" onClick={() => setStep(1)}>← 정책 다시 고르기</button><QuickProfile policy={selected} onComplete={(_profile, next) => {setResult(next); setStep(3);}} /></>}
    {step === 3 && result && <><ImpactLedger policy={selected} result={result} /><div className="result-actions"><WatchButton policyId={selected.id} /><button className="secondary-button" type="button" onClick={() => {setResult(null); setStep(1);}}>다른 정책 보기</button></div></>}
  </section>;
}

export function MaterialState({icon, title, children, action}: {icon: ReactNode; title: string; children: ReactNode; action?: ReactNode}) {
  return <div className="material-state"><div className="material-state__icon">{icon}</div><h2>{title}</h2><div>{children}</div>{action}</div>;
}
